import { Box } from "@chakra-ui/react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CapacityAllocationRepetitionTypeEnum,
  useUpdateAllocationDurationMutation,
  useUpdateAllocationItemDurationMutation,
} from "@src/__generated__/urql-graphql";
import { useResourcePlanningDailyViewStore } from "@src/components/modules/resource-planning/timeline/ResourcePlanningDayPage";
import {
  ALLOCATION_ITEM_MIN_HEIGHT,
  FIFTEEN_MINUTES_HEIGHT_OF_ALLOCATION,
  ONE_HOUR_HEIGHT_OF_ALLOCATION,
} from "@src/constants/planning";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { useStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { pxToNumber } from "@src/utils/pxToNumber";
import { secondsToTime, timeToSeconds } from "@src/utils/time";
import type { Time } from "@src/utils/types";
import {
  AllocationItem,
  type AllocationItemProps,
  calculateHeight,
} from "@src/widgets/AllocationItem/AllocationItem";
import {
  AllocationPopover,
  type AllocationPopoverRef,
} from "@src/widgets/AllocationPopover/AllocationPopover";
import { hoursToSeconds } from "date-fns";
import { observer } from "mobx-react-lite";
import { Resizable } from "re-resizable";
import {
  type CSSProperties,
  type FC,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebounce } from "react-use";

type SortableItemProps = AllocationItemProps;

function calculateDurationFromHeight(height: number): number {
  return hoursToSeconds(height / ONE_HOUR_HEIGHT_OF_ALLOCATION);
}

function calculateToTimeFromHeight({
  height,
  oldDuration,
  oldToTime,
}: {
  height: number;
  oldDuration: number;
  oldToTime: Time;
}) {
  const diffInTime = calculateDurationFromHeight(height) - oldDuration;
  const oldDurationSeconds = timeToSeconds(oldToTime) ?? 0;
  return secondsToTime(oldDurationSeconds + diffInTime);
}

const UPDATE_DEFER_TIME = 1000;

export const SortableItem: FC<SortableItemProps> = observer(
  function SortableItem(props) {
    const { workspaceStore } = useStore();
    const popoverRef = useRef<AllocationPopoverRef>(null);
    const resourcePlanningStore = useResourcePlanningDailyViewStore();
    const { userItem, day } = props;
    const isActiveItem =
      userItem.id === resourcePlanningStore.active?.userItem.id;
    const { isDesktop } = useScreenType();
    const isDisabled =
      !isDesktop || resourcePlanningStore.syncingIds.includes(userItem.id);
    const [isResizing, setIsResizing] = useState(false);
    const [height, setHeight] = useState(calculateHeight(userItem.total_time));
    const [calculatedTime, setCalculatedTime] = useState<number | undefined>(
      undefined,
    );
    const [calculatedToTime, setCalculatedToTime] = useState<Time | undefined>(
      userItem.to_time ?? undefined,
    );
    const viewSettings = workspaceStore.settings!.users_planning_view_settings;
    const isResizingEnabled = useMemo(() => {
      return (
        !resourcePlanningStore.active &&
        !!viewSettings?.adjust_allocation_height_by_duration
      );
    }, [
      resourcePlanningStore.active,
      viewSettings.adjust_allocation_height_by_duration,
    ]);

    const updateItemDuration = useUpdateAllocationItemDurationMutation()[1];
    const updateDuration = useUpdateAllocationDurationMutation()[1];

    const handleUpdateComponentHeight = useCallback(
      (totalTime: number, toTime: Time | null | undefined) => {
        setCalculatedTime(totalTime);
        if (toTime) {
          setCalculatedToTime(toTime);
        }
        setHeight(calculateHeight(totalTime));
      },
      [setCalculatedTime, setCalculatedToTime, setHeight, calculateHeight],
    );

    const onResizeStop = useCallback(() => {
      setIsResizing(false);

      if (!calculatedTime) return;
      if (!userItem?.stats) return;

      resourcePlanningStore.confirmDurationChangeModal.onOpen({
        newTime: calculatedToTime ?? calculatedTime,
        previousTime: userItem.to_time ?? userItem.total_time,
        onConfirm: () => {
          userItem.stats?.repetition ===
          CapacityAllocationRepetitionTypeEnum.Once
            ? updateDuration({
                id: userItem.stats?.capacity_allocation_id,
                total_time: calculatedTime,
                to_time: calculatedToTime,
                create_task_positions:
                  userItem.stats.create_task_positions ?? false,
              }).then(() => {
                userItem.total_time = calculatedTime;
                userItem.to_time = calculatedToTime;
                handleUpdateComponentHeight(calculatedTime, calculatedToTime);
              })
            : updateItemDuration({
                id: userItem.id,
                total_time: calculatedTime,
                to_time: calculatedToTime,
                user_id: userItem.user.id,
              }).then(() => {
                userItem.total_time = calculatedTime;
                userItem.to_time = calculatedToTime;
                handleUpdateComponentHeight(calculatedTime, calculatedToTime);
              });
        },
        onCancel: () => {
          handleUpdateComponentHeight(userItem.total_time, userItem.to_time);
        },
      });
    }, [
      calculatedTime,
      calculatedToTime,
      userItem.to_time,
      userItem.total_time,
      userItem.stats?.repetition,
      userItem.stats?.create_task_positions,
      userItem.stats?.capacity_allocation_id,
      handleUpdateComponentHeight,
    ]);

    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({
        id: userItem.id,
        disabled: isDisabled,
        data: {
          userItem,
          day,
          type: "item",
        },
        // TODO: add `satisfies DragItem` when we will have support for `satisfies`
      });

    const style: CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: isDisabled ? "not-allowed" : "initial",
    };

    useDebounce(
      () => {
        setCalculatedToTime(userItem.to_time ?? undefined);
      },
      UPDATE_DEFER_TIME,
      [userItem.to_time],
    );

    useDebounce(
      () => {
        setHeight(calculateHeight(userItem.total_time));
      },
      UPDATE_DEFER_TIME,
      [userItem.total_time],
    );

    return (
      <WrapComponent
        if={!resourcePlanningStore.active && isDesktop}
        with={(children) => (
          <AllocationPopover ref={popoverRef} userItem={userItem}>
            {children}
          </AllocationPopover>
        )}
      >
        <Box
          ref={setNodeRef}
          opacity={isActiveItem ? 0.6 : 1}
          {...listeners}
          {...attributes}
          style={style}
        >
          <WrapComponent
            // Wrap item in Resizable only if not dragging for better performance
            if={!resourcePlanningStore.active}
            with={(children) => (
              <Resizable
                as={Box}
                enable={{ bottom: true, top: false, right: false, left: false }}
                size={{
                  width: "auto",
                  height: isResizingEnabled
                    ? height
                    : ALLOCATION_ITEM_MIN_HEIGHT,
                }}
                style={{ overflow: isResizing ? "hidden" : undefined }}
                onResizeStart={() => {
                  setIsResizing(true);
                  setHeight(calculateHeight(userItem.total_time));
                }}
                onResize={(_e, _direction, ref) => {
                  const newHeight = pxToNumber(ref.style.height);
                  const newTime = calculateDurationFromHeight(newHeight);
                  setCalculatedTime(newTime);
                  userItem.computedTotalTime = newTime;
                  if (userItem.to_time) {
                    setCalculatedToTime(
                      calculateToTimeFromHeight({
                        height: newHeight,
                        oldDuration: userItem.total_time,
                        oldToTime: userItem.to_time,
                      }),
                    );
                  }
                  setHeight(newHeight);
                }}
                onResizeStop={onResizeStop}
                minHeight={
                  isResizing
                    ? FIFTEEN_MINUTES_HEIGHT_OF_ALLOCATION
                    : ALLOCATION_ITEM_MIN_HEIGHT
                }
                grid={[1, FIFTEEN_MINUTES_HEIGHT_OF_ALLOCATION]}
                handleComponent={{
                  bottom: (
                    <Box
                      pos="absolute"
                      zIndex="10"
                      right="0"
                      bottom="0"
                      left="0"
                      w="full"
                      h="4"
                      // data-no-dnd="true" to prevent drag on resize
                      data-no-dnd="true"
                      rounded="full"
                    />
                  ),
                }}
              >
                {children}
              </Resizable>
            )}
          >
            <AllocationItem
              userItem={props.userItem}
              day={props.day}
              calculatedTotalTime={calculatedTime}
              calculatedToTime={calculatedToTime}
              height={isResizingEnabled ? "full" : undefined}
              isResizing={isResizing}
              onClick={popoverRef.current?.onToggle}
              isLoading={resourcePlanningStore.deletingAllocationItemId.has(
                userItem.stats?.capacity_allocation_id ?? "",
              )}
              onAccept={(item) => {
                resourcePlanningStore.handleEdit(item, "allOccurrences");
              }}
              onAcceptForUser={(item) => {
                resourcePlanningStore.acceptImportedAllocationForUser(item);
              }}
              onDecline={({ stats }) => {
                if (stats?.capacity_allocation_id) {
                  resourcePlanningStore.declineImportedAllocation(
                    stats.capacity_allocation_id,
                  );
                }
              }}
              onDeclineForUser={(item) => {
                if (item.stats?.capacity_allocation_id) {
                  resourcePlanningStore.declineImportedAllocationForUser(
                    item.stats.capacity_allocation_id,
                    item.user.id,
                  );
                }
              }}
            />
          </WrapComponent>
        </Box>
      </WrapComponent>
    );
  },
);
